import * as Y from 'yjs';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useDiagramStore } from '../../stores/diagramStore';
import { Logger } from '../../utils/logger';
import { ShapeMapAdapter } from './ShapeMapAdapter';
import { ShapePersistenceGateway } from './shapeGeometry';

const logger = Logger.getLoggerWithPrefix('createDiagramProjection');

function mergeImageShapeAttrs(shape: Record<string, any>): Record<string, any> {
  const fromProps = (shape.props?.attrs as Record<string, any> | undefined) || {};
  const fromTop = (shape.attrs as Record<string, any> | undefined) || {};
  return { ...fromProps, ...fromTop };
}

function isStuckCanvasImageUpload(shape: Record<string, any>): boolean {
  if (shape.type !== 'image') return false;
  const attrs = mergeImageShapeAttrs(shape);
  if (attrs.uploadState !== 'uploading') return false;
  const key = typeof attrs.objectKey === 'string' ? attrs.objectKey.trim() : '';
  return key.length === 0;
}

function repairStuckCanvasImageUploadsInAdapter(doc: Y.Doc, shapes: ShapePersistenceGateway): number {
  const ids: string[] = [];
  shapes.forEach((shape, id) => {
    if (isStuckCanvasImageUpload(shape)) ids.push(id);
  });
  if (ids.length === 0) return 0;
  doc.transact(() => {
    for (const id of ids) {
      const shape = shapes.get(id);
      if (!shape || !isStuckCanvasImageUpload(shape)) continue;
      shapes.delete(id);
    }
  }, 'local');
  return ids.length;
}

export interface ShapeBase {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  zIndex?: number;
  style?: any;
  visible?: boolean;
  locked?: boolean;
  props?: any;
}

export interface ConnectorBase {
  id: string;
  type: string;
  sourceShapeId: string;
  targetShapeId: string;
  routingType?: 'straight' | 'orthogonal' | 'orthogonalRounded' | 'bezier' | 'rounded' | 'manual';
  waypoints?: { x: number; y: number }[];
  style?: any;
  props?: any;
}

export interface ProjectionStore {
  shapes: Map<string, ShapeBase>;
  connectors: Map<string, ConnectorBase>;
  setShapes: (shapes: Map<string, ShapeBase>) => void;
  setConnectors: (connectors: Map<string, ConnectorBase>) => void;
}

export function createDiagramProjection(doc: Y.Doc) {
  const useStore = create<ProjectionStore>()(
    subscribeWithSelector((set) => ({
      shapes: new Map(),
      connectors: new Map(),
      setShapes: (shapes) => set({ shapes }),
      setConnectors: (connectors) => set({ connectors }),
    }))
  );

  const shapesAdapter = new ShapeMapAdapter(doc.getMap('shapes'));
  const shapeGateway = new ShapePersistenceGateway(shapesAdapter);
  const connectorsMap = doc.getMap<ConnectorBase>('connectors');

  const bootstrap = () => {
    const repaired = repairStuckCanvasImageUploadsInAdapter(doc, shapeGateway);
    if (repaired > 0) {
      try {
        logger.info('Repaired stuck canvas image uploads after load', { count: repaired });
      } catch {}
    }

    const shapes = new Map<string, ShapeBase>();
    const connectors = new Map<string, ConnectorBase>();

    shapesAdapter.forEach((shape, id) => {
      shapes.set(id, shape as ShapeBase);
    });

    connectorsMap.forEach((connector, id) => {
      connectors.set(id, connector);
    });

    useStore.getState().setShapes(shapes);
    useStore.getState().setConnectors(connectors);
    try {
      logger.debug('Debug SDI: Projection bootstrap', {
        shapes: shapes.size,
        connectors: connectors.size
      });
    } catch {}
  };

  const handleShapeChangesDeep = (events: Y.YEvent<any>[]) => {
    const changedIds = new Set<string>();
    const deletedIds = new Set<string>();

    for (const event of events) {
      if (event.path.length === 0 && event instanceof Y.YMapEvent) {
        event.changes.keys.forEach((change, key) => {
          if (change.action === 'delete') {
            deletedIds.add(key);
          } else {
            changedIds.add(key);
          }
        });
      } else if (event.path.length >= 1) {
        changedIds.add(String(event.path[0]));
      }
    }

    if (changedIds.size === 0 && deletedIds.size === 0) return;

    const currentShapes = useStore.getState().shapes;
    const newShapes = new Map(currentShapes);

    for (const id of deletedIds) {
      newShapes.delete(id);
    }
    for (const id of changedIds) {
      const shape = shapesAdapter.get(id);
      if (shape) {
        newShapes.set(id, shape as ShapeBase);
      }
    }

    useStore.getState().setShapes(newShapes);
  };

  const handleConnectorChanges = (event: Y.YMapEvent<ConnectorBase>) => {
    const currentConnectors = useStore.getState().connectors;
    const newConnectors = new Map(currentConnectors);

    event.changes.keys.forEach((change, key) => {
      if (change.action === 'add' || change.action === 'update') {
        const connector = connectorsMap.get(key);
        if (connector) {
          newConnectors.set(key, connector);
        }
      } else if (change.action === 'delete') {
        newConnectors.delete(key);
      }
    });

    useStore.getState().setConnectors(newConnectors);
  };

  shapesAdapter.raw.observeDeep(handleShapeChangesDeep);
  connectorsMap.observe(handleConnectorChanges);

  bootstrap();

  const insertShape = (shape: ShapeBase) => {
    doc.transact(() => {
      shapeGateway.create(shape as unknown as Record<string, unknown>);
    }, 'local');
  };

  const updateShape = (id: string, updates: Partial<ShapeBase>) => {
    try {
      const draggingIds = useDiagramStore.getState().draggingIds;
      const isPositionUpdate = typeof updates.x === 'number' || typeof updates.y === 'number' || typeof updates.width === 'number' || typeof updates.height === 'number';
      if (isPositionUpdate && draggingIds.has(id)) {
        return;
      }
    } catch (_) {
      // store not initialized
    }
    doc.transact(() => {
      const existing = shapesAdapter.get(id);
      if (!existing) return;

      // Build only the actually-changed fields for patch().
      // This avoids writing unchanged style/props (which are large objects)
      // when only primitive fields like x/y changed.
      const changes: Record<string, any> = {};

      for (const [k, v] of Object.entries(updates)) {
        if (k === 'style' || k === 'props') continue;
        changes[k] = v;
      }

      if (updates.style) {
        changes.style = { ...(existing.style || {}), ...updates.style };
      }

      if ((updates as any).props) {
        const prevProps: any = existing.props || {};
        const nextProps: any = (updates as any).props;
        const mergedProps = { ...prevProps, ...nextProps };
        mergedProps.attrs = { ...(prevProps?.attrs || {}), ...(nextProps?.attrs || {}) };
        changes.props = mergedProps;
      }

      const rejectedKeys = shapeGateway.patch(id, changes);
      if (rejectedKeys.length > 0) {
        logger.warn('Rejected non-finite projection geometry update', { id, keys: rejectedKeys });
      }
    }, 'local');
  };

  const batchUpdateShapes = (updates: Array<{ id: string; updates: Partial<ShapeBase> }>) => {
    if (updates.length === 0) return;

    let draggingIds: Set<string>;
    try {
      draggingIds = useDiagramStore.getState().draggingIds;
    } catch (_) {
      draggingIds = new Set();
    }

    const filteredUpdates = updates.filter(({ id, updates: shapeUpdates }) => {
      const isPositionUpdate = typeof shapeUpdates.x === 'number' || typeof shapeUpdates.y === 'number' || typeof shapeUpdates.width === 'number' || typeof shapeUpdates.height === 'number';
      return !(isPositionUpdate && draggingIds.has(id));
    });

    if (filteredUpdates.length === 0) return;

    doc.transact(() => {
      filteredUpdates.forEach(({ id, updates: shapeUpdates }) => {
        const existing = shapesAdapter.get(id);
        if (!existing) return;

        // Build only the actually-changed fields for patch().
        const changes: Record<string, any> = {};

        for (const [k, v] of Object.entries(shapeUpdates)) {
          if (k === 'style' || k === 'props') continue;
          changes[k] = v;
        }

        if (shapeUpdates.style) {
          changes.style = { ...(existing.style || {}), ...shapeUpdates.style };
        }

        if ((shapeUpdates as any).props) {
          const prevProps: any = existing.props || {};
          const nextProps: any = (shapeUpdates as any).props;
          const mergedProps = { ...prevProps, ...nextProps };
          mergedProps.attrs = { ...(prevProps?.attrs || {}), ...(nextProps?.attrs || {}) };
          changes.props = mergedProps;
        }

        const rejectedKeys = shapeGateway.patch(id, changes);
        if (rejectedKeys.length > 0) {
          logger.warn('Rejected non-finite projection geometry update', { id, keys: rejectedKeys });
        }
      });
    }, 'local');
  };

  const deleteShape = (id: string) => {
    doc.transact(() => {
      shapeGateway.delete(id);
    }, 'local');
  };

  const insertConnector = (connector: ConnectorBase) => {
    doc.transact(() => {
      connectorsMap.set(connector.id, connector);
    }, 'local');
  };

  const updateConnector = (id: string, updates: Partial<ConnectorBase>) => {
    doc.transact(() => {
      const existing = connectorsMap.get(id);
      if (existing) {
        const merged = { ...existing, ...updates };
        connectorsMap.set(id, merged);
      }
    }, 'local');
  };

  const deleteConnector = (id: string) => {
    doc.transact(() => {
      connectorsMap.delete(id);
    }, 'local');
  };

  const cleanup = () => {
    shapesAdapter.raw.unobserveDeep(handleShapeChangesDeep);
    connectorsMap.unobserve(handleConnectorChanges);
  };

  return {
    store: useStore,
    mutations: {
      insertShape,
      updateShape,
      batchUpdateShapes,
      deleteShape,
      insertConnector,
      updateConnector,
      deleteConnector,
    },
    cleanup,
  };
}
